import ErrorIcon from '@mui/icons-material/Error';
import { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { BaseQueryFn, MutationDefinition } from '@reduxjs/toolkit/query';
import { Modal } from 'antd';
import { QueryResponse } from 'lib/rtkQuery/types';
import { useCallback } from 'react';
import { messageUtils } from 'utils';
import { ObjectType } from 'utils/types';

type UseHandleDeleteRecordProps<T> = {
  actionLabel: string;
  deleteMutationFn: MutationTrigger<
    MutationDefinition<T, BaseQueryFn, string, QueryResponse<string>, 'baseApi'>
  >;
  isDeleting: boolean;
  deleteRecordKey: string;
  payloadDataKey?: string;
  onDeletedSuccess?: () => void;
};

const { confirm } = Modal;

/**
 *
 * Hook handle việc xóa 1 record của 1 bảng.
 * @param actionLabel (string) : module gọi custom hook, dùng để custom label
 * @param deleteMutationFn: rtk mutation function, gọi khi người dùng confirm.
 * @param isDeleting (boolean): rtk mutation state isLoading
 * @param payloadDataKey (string): tên tham số cần truyền cho api endpoint,
 * @param deleteRecordKey (string): tên cột chứa giá trị của tham số cần truyền cho api endpoint,
 * @returns onDeleteRecord (void)
 */
function useHandleDeleteRecord<T>({
  actionLabel,
  deleteMutationFn,
  isDeleting,
  deleteRecordKey,
  payloadDataKey = 'id',
  onDeletedSuccess,
}: UseHandleDeleteRecordProps<T>) {
  const { message } = messageUtils;

  const onDeleteRecord = useCallback(
    (record: ObjectType) => {
      if (!record?.[`${deleteRecordKey}`]) return;

      confirm({
        title: (
          <div className="ant-modal-confirm-heading">
            <ErrorIcon className="icon-danger" />
            <strong className="font-semibold">
              Bạn có chắc chắn muốn xóa {actionLabel} này?
            </strong>
          </div>
        ),
        icon: null,
        content: (
          <p className="mb-4">
            Thao tác này sẽ xoá dữ liệu {actionLabel} và bạn sẽ không thể khôi
            phục được như ban đầu. Bạn có muốn tiếp tục?
          </p>
        ),
        onOk: async () => {
          try {
            await deleteMutationFn({
              [`${payloadDataKey}`]: record?.[`${deleteRecordKey}`],
            } as T).unwrap();
            void message.success(`Xóa ${actionLabel} thành công`);
            onDeletedSuccess?.();
          } catch (err) {}
        },
        okButtonProps: {
          disabled: isDeleting,
          danger: true,
        },
        cancelButtonProps: {
          className:
            '!border !border-solid !border-error !text-error hover:opacity-75',
        },
        centered: true,
      });
    },
    [
      deleteRecordKey,
      actionLabel,
      isDeleting,
      deleteMutationFn,
      payloadDataKey,
      message,
      onDeletedSuccess,
    ]
  );

  return { onDeleteRecord };
}

export default useHandleDeleteRecord;
